import { useState, useCallback, Fragment } from 'react';
import DateSelector from '../components/analytics/DateSelector';
import DeviceFilter from '../components/analytics/DeviceFilter';
import StoreFilter from '../components/analytics/StoreFilter';
import SessionDetail from '../components/analytics/session/SessionDetail';
import type { SessionSummary, SessionFullDetail } from '../types/analyticstypes';
import {
  defaultDateRange,
  freshenRangeIfPreset,
} from '../utils/analytics/dateRange';
import type { DateRange, PresetKey } from '../utils/analytics/dateRange';
import {
  formatSec,
  formatPrice,
  formatLocalDate,
  formatLocalDateTime,
} from '../utils/analytics/format';
import { getSessions, getSessionDetail } from '../services/analyticsservice';
import ExportSessionsCSV from '../components/analytics/session/ExportSessionsCSV';

type SortKey =
  | 'timestamp'
  | 'browseDurationSec'
  | 'kdsDurationSec'
  | 'itemCount'
  | 'extrasCount'
  | 'totalPrice'
  | 'rating'
  | 'loyalty';
type SortDir = 'asc' | 'desc';

export default function AnalyticsSessionsPage() {
  const [range, setRange] = useState<DateRange>(() => defaultDateRange());
  const [presetKey, setPresetKey] = useState<PresetKey | null>('today');
  const [storeNumber, setStoreNumber] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [sessionDetails, setSessionDetails] = useState<Record<string, SessionFullDetail>>({});
  const [sortKey, setSortKey] = useState<SortKey>('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = useCallback(
    (key: SortKey) => {
      setSortDir((prev) => (sortKey === key ? (prev === 'desc' ? 'asc' : 'desc') : 'desc'));
      setSortKey(key);
    },
    [sortKey]
  );

  const sortedSessions = [...sessions].sort((a, b) => {
    let aVal: number;
    let bVal: number;
    switch (sortKey) {
      case 'timestamp':
        aVal = new Date(a.timestamp).getTime();
        bVal = new Date(b.timestamp).getTime();
        break;
      case 'browseDurationSec':
        aVal = a.browseDurationSec ?? -1;
        bVal = b.browseDurationSec ?? -1;
        break;
      case 'kdsDurationSec':
        aVal = a.kdsDurationSec ?? -1;
        bVal = b.kdsDurationSec ?? -1;
        break;
      case 'itemCount':
        aVal = a.itemCount ?? 0;
        bVal = b.itemCount ?? 0;
        break;
      case 'extrasCount':
        aVal = (a.addOnCount ?? 0) + (a.upsellCount ?? 0);
        bVal = (b.addOnCount ?? 0) + (b.upsellCount ?? 0);
        break;
      case 'totalPrice':
        aVal = Number(a.totalPrice) || 0;
        bVal = Number(b.totalPrice) || 0;
        break;
      case 'rating': {
        const aRatio =
          a.feedbackRating != null && a.feedbackMaxRating
            ? a.feedbackRating / a.feedbackMaxRating
            : -1;
        const bRatio =
          b.feedbackRating != null && b.feedbackMaxRating
            ? b.feedbackRating / b.feedbackMaxRating
            : -1;
        aVal = aRatio;
        bVal = bRatio;
        break;
      }
      case 'loyalty':
        aVal = (a.loyaltyCustomer ? 2 : 0) + (a.loyaltyEnrollAttempted ? 1 : 0);
        bVal = (b.loyaltyCustomer ? 2 : 0) + (b.loyaltyEnrollAttempted ? 1 : 0);
        break;
    }
    return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
  });

  const loadSessions = useCallback(async () => {
    const liveRange = freshenRangeIfPreset(range, presetKey);
    if (liveRange !== range) setRange(liveRange);

    setLoading(true);
    setError(null);
    try {
      const data = await getSessions({
        from: liveRange.from,
        to: liveRange.to,
        storeNumber: storeNumber || undefined,
        deviceId: deviceId || undefined,
      });
      setSessions(data);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to fetch sessions';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, range, presetKey, storeNumber]);

  const toggleExpand = useCallback(
    async (session: SessionSummary) => {
      const key = session.id;
      if (expandedSession === key) {
        setExpandedSession(null);
        return;
      }
      setExpandedSession(key);

      if (!sessionDetails[key]) {
        try {
          const data = await getSessionDetail(session.sessionId, session.id);
          setSessionDetails((prev) => ({ ...prev, [key]: data }));
        } catch {
          // Silently fail detail fetch
        }
      }
    },
    [expandedSession, sessionDetails]
  );

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Sessions</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <DateSelector
            value={range}
            presetKey={presetKey}
            onChange={(r, k) => {
              setRange(r);
              setPresetKey(k);
            }}
          />
          <StoreFilter value={storeNumber} onChange={setStoreNumber} />
          <DeviceFilter value={deviceId} onChange={setDeviceId} />
          <button
            onClick={loadSessions}
            disabled={loading}
            className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Loading...' : 'Load'}
          </button>
          <ExportSessionsCSV sessions={sessions} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6">{error}</div>
      )}

      {sessions.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-medium text-gray-600">Store</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Device</th>
                <SortHeader label="Time" sortKey="timestamp" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Browse" sortKey="browseDurationSec" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Kitchen" sortKey="kdsDurationSec" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Items" sortKey="itemCount" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Extras" sortKey="extrasCount" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Total" sortKey="totalPrice" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <th className="text-left px-4 py-3 font-medium text-gray-600">Result</th>
                <SortHeader label="Rating" sortKey="rating" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Loyalty" sortKey="loyalty" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <th className="text-left px-4 py-3 font-medium text-gray-600">Order #</th>
              </tr>
            </thead>
            <tbody>
              {sortedSessions.map((s) => {
                const isExpanded = expandedSession === s.id;
                const detail = sessionDetails[s.id];
                return (
                  <Fragment key={s.id}>
                    <tr
                      className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50' : ''}`}
                      onClick={() => toggleExpand(s)}
                    >
                      <td className="px-4 py-3 font-mono text-xs">{s.storeNumber || '--'}</td>
                      <td className="px-4 py-3 font-mono text-xs">{s.deviceId}</td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        {formatLocalDate(s.timestamp)} {formatLocalDateTime(s.timestamp)}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {s.journeyCorrupt ? (
                          <span className="text-amber-500" title="Journey data unavailable">--</span>
                        ) : s.browseDurationSec != null ? formatSec(s.browseDurationSec) : '--'}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {s.kdsDurationSec != null ? formatSec(s.kdsDurationSec) : '--'}
                      </td>
                      <td className="px-4 py-3">{s.itemCount ?? '--'}</td>
                      <td className="px-4 py-3">
                        <ExtrasCell addOnCount={s.addOnCount} upsellCount={s.upsellCount} />
                      </td>
                      <td className="px-4 py-3">
                        {s.totalPrice != null ? formatPrice(s.totalPrice) : '--'}
                      </td>
                      <td className="px-4 py-3">
                        <SessionResultBadge status={s.status} paymentStatus={s.paymentStatus} endReason={s.endReason} />
                      </td>
                      <td className="px-4 py-3">
                        <RatingBadge rating={s.feedbackRating} max={s.feedbackMaxRating} status={s.status} />
                      </td>
                      <td className="px-4 py-3">
                        <LoyaltyCell loyalty={s.loyaltyCustomer} enroll={s.loyaltyEnrollAttempted} />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{s.orderNumber || '--'}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={12} className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                          {detail ? (
                            <SessionDetail detail={detail} />
                          ) : (
                            <p className="text-gray-500 text-sm">Loading session details...</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && sessions.length === 0 && !error && (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg">No sessions loaded</p>
          <p className="text-sm mt-1">Select a time range and click Load to view sessions</p>
        </div>
      )}
    </div>
  );
}

function SortHeader({
  label,
  sortKey: key,
  currentKey,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const isActive = currentKey === key;
  return (
    <th
      className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-gray-900 transition-colors"
      onClick={() => onSort(key)}
    >
      <span className="flex items-center gap-1">
        {label}
        {isActive && (
          <span className="text-blue-500 text-xs">{dir === 'desc' ? '▼' : '▲'}</span>
        )}
      </span>
    </th>
  );
}

function SessionResultBadge({
  status,
  paymentStatus,
  endReason,
}: {
  status?: string;
  paymentStatus?: string;
  endReason?: string;
}) {
  if (status === 'COMPLETED') {
    if (paymentStatus === 'PAID') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium text-green-600 bg-green-50">PAID</span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium text-red-600 bg-red-50">UNPAID</span>
    );
  }
  if (status === 'ABANDONED') {
    if (endReason === 'timeout') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium text-orange-600 bg-orange-50">TIMEOUT</span>
      );
    }
    if (endReason === 'cancel') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium text-red-600 bg-red-50">CANCELLED</span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium text-gray-600 bg-gray-50">ABANDONED</span>
    );
  }
  return <span className="text-xs text-gray-400">{status || '--'}</span>;
}

function RatingBadge({
  rating,
  max,
  status,
}: {
  rating?: number | null;
  max?: number | null;
  status?: string;
}) {
  if (status === 'ABANDONED') {
    return <span className="text-xs text-gray-300">·</span>;
  }
  if (rating == null || !max) {
    return (
      <span
        className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-50 text-gray-400 border border-gray-200"
        title="No feedback provided"
      >
        —
      </span>
    );
  }
  const ratio = rating / max;
  let cls = 'bg-gray-50 text-gray-700 border-gray-200';
  if (ratio >= 0.8) cls = 'bg-green-50 text-green-700 border-green-200';
  else if (ratio >= 0.4) cls = 'bg-amber-50 text-amber-700 border-amber-200';
  else cls = 'bg-red-50 text-red-700 border-red-200';
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}
      title={`Customer rating ${rating} out of ${max}`}
    >
      {rating}/{max}
    </span>
  );
}

function LoyaltyCell({ loyalty, enroll }: { loyalty?: boolean; enroll?: boolean }) {
  if (!loyalty && !enroll) {
    return <span className="text-xs text-gray-400">--</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {loyalty && (
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200"
          title="Loyalty customer"
        >
          ⭐ Loyalty
        </span>
      )}
      {enroll && (
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
          title="Enrollment attempted"
        >
          ✎ Enroll
        </span>
      )}
    </div>
  );
}

function ExtrasCell({
  addOnCount,
  upsellCount,
}: {
  addOnCount?: number;
  upsellCount?: number;
}) {
  const adds = addOnCount ?? 0;
  const ups = upsellCount ?? 0;
  if (adds === 0 && ups === 0) {
    return <span className="text-xs text-gray-400">--</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {adds > 0 && (
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
          title={`${adds} add-on${adds !== 1 ? 's' : ''}`}
        >
          {adds} Add-on{adds !== 1 ? 's' : ''}
        </span>
      )}
      {ups > 0 && (
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200"
          title={`${ups} upsell${ups !== 1 ? 's' : ''}`}
        >
          {ups} Upsell{ups !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}
